import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { AlertCircle, Expand, Loader2, Minus, ScanLine, X, Plus } from 'lucide-react';

/**
 * Shared responsive viewer for Mermaid definitions embedded in any article or
 * workspace. It owns rendering, safe SVG sizing, zoom, fit, and fullscreen
 * inspection so consumers only provide a Mermaid definition.
 */
export interface MermaidDiagramProps {
  definition?: string;
  /** A trusted, app-generated SVG. It uses the same viewer controls as Mermaid. */
  svgMarkup?: string;
  ariaLabel?: string;
}

const normalizeSvgDimensions = (svg: string): string => {
  return svg.replace(/<svg\b([^>]*)>/, (_match, attributes) => {
    const withoutSizing = attributes
      .replace(/\swidth="[^"]*"/g, '')
      .replace(/\sheight="[^"]*"/g, '')
      .replace(/\sstyle="[^"]*"/g, '')
      .replace(/\spreserveAspectRatio="[^"]*"/g, '');
    return `<svg${withoutSizing} width="100%" style="display:block;width:100%;height:auto;max-width:none" preserveAspectRatio="xMidYMid meet">`;
  });
};

/** Renders trusted, app-owned Mermaid definitions without enabling Mermaid HTML or links. */
const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ definition, svgMarkup, ariaLabel = 'Expanded diagram' }) => {
  const reactId = useId().replace(/:/g, '');
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [zoom, setZoom] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    if (svgMarkup) {
      setSvg(normalizeSvgDimensions(svgMarkup));
      setError('');
      return () => { cancelled = true; };
    }

    if (!definition) {
      setError('No diagram definition was supplied.');
      return () => { cancelled = true; };
    }

    const render = async () => {
      try {
        const { default: mermaid } = await import('mermaid');
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: 'base',
          themeVariables: {
            background: '#111827',
            primaryColor: '#153f36',
            primaryTextColor: '#e5e7eb',
            primaryBorderColor: '#42c59a',
            lineColor: '#9ca3af',
            secondaryColor: '#1f2937',
            tertiaryColor: '#172033',
            textColor: '#e5e7eb',
            actorBkg: '#153f36',
            actorBorder: '#42c59a',
            actorTextColor: '#e5e7eb',
            signalColor: '#d1d5db',
            signalTextColor: '#e5e7eb',
            noteBkgColor: '#1f2937',
            noteBorderColor: '#4b5563',
            noteTextColor: '#e5e7eb',
          },
          // Keep short flowchart labels compact. The viewport measurement below
          // supplies outer breathing room, so large node-internal padding is
          // unnecessary and makes simple diagrams look oversized.
          flowchart: { useMaxWidth: false, padding: 10, nodeSpacing: 24, rankSpacing: 24 },
          sequence: { useMaxWidth: false },
        });
        const result = await mermaid.render(`mermaid-diagram-${reactId}`, definition.replace(/\\n/g, '<br/>'));
        if (!cancelled) {
          setSvg(normalizeSvgDimensions(result.svg));
          setError('');
        }
      } catch (renderError) {
        if (!cancelled) {
          setError(renderError instanceof Error ? renderError.message : 'Unable to render this diagram.');
        }
      }
    };

    void render();
    return () => { cancelled = true; };
  }, [definition, reactId, svgMarkup]);

  // Mermaid sometimes reports a viewBox that ends on a node or connector.
  // Measure the browser's real graphic bounds after mounting, then make the
  // SVG viewport match those bounds. This avoids both clipping and lopsided
  // "extra space" from fixed, one-size-fits-all padding.
  useLayoutEffect(() => {
    if (!svg || !canvasRef.current) return undefined;

    const frame = requestAnimationFrame(() => {
      const svgElement = canvasRef.current?.querySelector('svg');
      if (!svgElement) return;

      try {
        const bounds = svgElement.getBBox();
        if (!bounds.width || !bounds.height) return;

        const horizontalPadding = Math.max(36, bounds.width * 0.08);
        const verticalPadding = Math.max(32, bounds.height * 0.1);
        const viewBox = [
          bounds.x - horizontalPadding,
          bounds.y - verticalPadding,
          bounds.width + horizontalPadding * 2,
          bounds.height + verticalPadding * 2,
        ].map((value) => Number(value.toFixed(2))).join(' ');

        setSvg((current) => current.replace(/viewBox="[^"]*"/, `viewBox="${viewBox}"`));
      } catch {
        // Keep Mermaid's original viewport if the browser cannot measure it.
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [svg]);

  // A fullscreen diagram has its own scrollable viewport. Prevent scroll
  // chaining from moving the article behind the modal when that viewport
  // reaches an edge.
  useEffect(() => {
    if (!expanded) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousDocumentOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousDocumentOverflow;
    };
  }, [expanded]);

  if (error) {
    return (
      <div className="my-6 flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>The diagram could not be rendered.</span>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className="my-6 flex min-h-32 items-center justify-center rounded-lg border border-gray-700 bg-gray-900/70 text-sm text-gray-400">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Rendering diagram…
      </div>
    );
  }

  const minZoom = 0.5;
  const maxZoom = 2.5;
  const decreaseZoom = () => setZoom((current) => Math.max(minZoom, Number((current - 0.1).toFixed(1))));
  const increaseZoom = () => setZoom((current) => Math.min(maxZoom, Number((current + 0.1).toFixed(1))));
  const openExpanded = () => {
    setZoom(1);
    setExpanded(true);
  };
  const closeExpanded = () => {
    setZoom(1);
    setExpanded(false);
  };
  const viewBoxMatch = svg.match(/viewBox="[^"\s]+\s+[^"\s]+\s+([^"\s]+)\s+([^"\s]+)"/);
  const isWideDiagram = viewBoxMatch
    ? Number(viewBoxMatch[1]) >= Number(viewBoxMatch[2])
    : false;
  // A short vertical flow is readable at a much smaller width. Rendering every
  // SVG at the article's full width turns a five-step flow into an oversized
  // poster, so reserve the full width for genuinely wide diagrams.
  const isCompactDiagram = viewBoxMatch
    ? Number(viewBoxMatch[1]) / Number(viewBoxMatch[2]) < 1.35
    : false;
  const expandedCanvasClassName = isWideDiagram
    ? 'flex items-center justify-center [&_svg]:!h-auto [&_svg]:!w-full [&_svg]:!max-h-full'
    : 'flex items-center justify-center [&_svg]:!h-full [&_svg]:!w-auto [&_svg]:!max-w-full';
  const diagram = (isExpandedView = false) => (
    <div className={`${isExpandedView ? 'h-full min-h-0 overscroll-contain' : ''} overflow-auto rounded-lg border border-gray-700 bg-gray-950/40`}>
      <div
        ref={canvasRef}
        className={`${isExpandedView ? expandedCanvasClassName : 'mx-auto [&_svg]:h-auto [&_svg]:w-full'} shrink-0 p-3 sm:p-4 transition-[width] duration-150`}
        style={{
          width: isExpandedView
            ? `${zoom * 100}%`
            : isCompactDiagram
              ? `min(${zoom * 100}%, ${zoom * 22}rem)`
              : `${zoom * 100}%`,
          height: isExpandedView ? `${zoom * 100}%` : undefined,
        }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </div>
  );
  const controls = (isExpanded: boolean) => (
    <div className="flex items-center gap-1" role="group" aria-label="Diagram controls">
      <button type="button" onClick={decreaseZoom} disabled={zoom <= minZoom} className="rounded p-1.5 text-gray-300 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40" aria-label="Zoom out">
        <Minus className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => setZoom(1)} className="rounded px-2 py-1 text-xs font-medium text-gray-300 hover:bg-gray-700" aria-label="Fit diagram to view">
        <ScanLine className="mr-1 inline h-3.5 w-3.5" /> Fit
      </button>
      <button type="button" onClick={increaseZoom} disabled={zoom >= maxZoom} className="rounded p-1.5 text-gray-300 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40" aria-label="Zoom in">
        <Plus className="h-4 w-4" />
      </button>
      {isExpanded ? (
        <button type="button" onClick={closeExpanded} className="rounded p-1.5 text-gray-300 hover:bg-gray-700" aria-label="Close expanded diagram">
          <X className="h-4 w-4" />
        </button>
      ) : (
        <button type="button" onClick={openExpanded} className="rounded p-1.5 text-gray-300 hover:bg-gray-700" aria-label="Expand diagram">
          <Expand className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  return (
    <>
      {!expanded && (
        <figure className="my-6 rounded-xl border border-gray-700 bg-gray-900/70 p-3 shadow-sm">
          <div className="mb-2 flex justify-end">
            {controls(false)}
          </div>
          {diagram()}
        </figure>
      )}
      {expanded && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-950/90 p-2 sm:p-3" role="dialog" aria-modal="true" aria-label={ariaLabel}>
          <div className="flex h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] max-w-none flex-col rounded-xl border border-gray-700 bg-gray-900 p-4 shadow-2xl sm:h-[calc(100dvh-1.5rem)] sm:w-[calc(100vw-1.5rem)]">
            <div className="mb-2 flex justify-end">
              {controls(true)}
            </div>
            <div className="min-h-0 flex-1">{diagram(true)}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default MermaidDiagram;
